/**
 * Compliance Queue — operational-queue spec helpers.
 * Case ID CMP-######. Decision ID format NOT DEFINED (demo uses CMP-DEC-####).
 * Status: New | Under Review | Decided (while Archive move unfinished).
 * No Awaiting Information status — info requests stay Under Review.
 * Types: Licence | Badge | Ticket. Case Types: New | Renewal | Replacement.
 */

export const COMPLIANCE_QUEUE_ROUTE = '/admin/verification/queue';

export const COMPLIANCE_QUEUE_STATUSES = ['New', 'Under Review', 'Decided'];
export const COMPLIANCE_TYPES = ['Licence', 'Badge', 'Ticket'];
export const CASE_TYPES = ['New', 'Renewal', 'Replacement'];
export const FINAL_OUTCOME_OPTIONS = ['Approved', 'Rejected'];

export const COMPLIANCE_DECISION_FORM_ID = 'FORM-000039';
export const COMPLIANCE_DECISION_FORM_DISPLAY = 'FORM-000039 — Compliance Decision Form';

export const SUPPORT_TEAM_MEMBERS = [
  'Jordan Lee',
  'Mia Chen',
  'Olivia Smith',
  'Daniel Price',
];
export const CURRENT_STAFF_MEMBER = 'Jordan Lee';

const TRADE_CATEGORIES = [
  'Plumbing',
  'Electrical',
  'Building',
  'Gas Fitting',
  'Carpentry',
  'Roofing',
  'Painting',
  'Landscaping',
];

const COMPLIANCE_ITEMS_BY_TYPE = {
  Licence: [
    { id: 'LIC-0001', name: 'Electrical Licence', tradeCategory: 'Electrical' },
    { id: 'LIC-0002', name: 'Plumbing Licence', tradeCategory: 'Plumbing' },
    { id: 'LIC-0003', name: 'Gas Fitting Licence', tradeCategory: 'Plumbing' },
  ],
  Badge: [
    { id: 'BDG-0001', name: 'First Aid', tradeCategory: 'All Trades' },
    { id: 'BDG-0002', name: 'Industry Membership', tradeCategory: 'All Trades' },
    { id: 'BDG-0003', name: 'Working with Children Check', tradeCategory: 'All Trades' },
  ],
  Ticket: [
    { id: 'TKT-0001', name: 'White Card', tradeCategory: 'All Trades' },
    { id: 'TKT-0002', name: 'Working at Heights', tradeCategory: 'All Trades' },
    { id: 'TKT-0003', name: 'Confined Spaces', tradeCategory: 'All Trades' },
  ],
};

const parseSampleDate = (day, monthShort, year, hour, minute, isPm) => {
  const months = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
  };
  let h = hour;
  if (isPm && h < 12) h += 12;
  if (!isPm && h === 12) h = 0;
  return new Date(year, months[monthShort], day, h, minute, 0, 0).toISOString();
};

const dash = (value) => {
  if (value == null) return '—';
  const text = String(value).trim();
  return text === '' || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined'
    ? '—'
    : text;
};

export const formatComplianceDateTime = (value) => {
  if (!value || value === '—') return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
};

export const formatAssignedDisplay = (raw) => {
  if (!raw || String(raw).toLowerCase() === 'unassigned' || String(raw).trim() === '—') {
    return 'Unassigned';
  }
  return (
    String(raw)
      .replace(/^Fraud\s*&\s*Security — /i, '')
      .replace(/^(Admin|Compliance|Support|Moderator|Owner|Finance|Development|Team Member) — /i, '')
      .replace(/^Admin: /, '')
      .replace(/\s*\([^)]*\)\s*$/, '')
      .split(' (')[0]
      .trim() || 'Unassigned'
  );
};

export const normalizeSupportTeam = (value) => formatAssignedDisplay(value);

export const normalizeQueueStatus = (status) => {
  const raw = String(status || '').trim();
  if (!raw) return 'New';
  if (raw === 'New') return 'New';
  if (
    raw === 'Under Review' ||
    raw === 'In Review' ||
    raw === 'Active' ||
    raw === 'Assigned' ||
    raw === 'Awaiting Information' ||
    raw === 'Information Requested'
  ) {
    return 'Under Review';
  }
  if (raw === 'Decided' || raw === 'Resolved' || raw === 'Closed' || raw === 'Archived') {
    return 'Decided';
  }
  return raw;
};

/** New ↔ unassigned; Under Review ↔ assigned Support Team Member. */
export const coerceStatusAndAssignment = (row = {}) => {
  let status = normalizeQueueStatus(row.case_status || row.status || row.verification_status);
  const supportTeamMember = normalizeSupportTeam(
    row.support_team_member || row.supportTeamMember || row.assigned_to || row.assignedTo
  );
  const assigned = supportTeamMember !== 'Unassigned';
  if (status !== 'Decided') {
    if (assigned && status === 'New') status = 'Under Review';
    else if (!assigned && status === 'Under Review') status = 'New';
  }
  return {
    case_status: status,
    status,
    verification_status: status,
    support_team_member: assigned ? supportTeamMember : null,
    supportTeamMember: assigned ? supportTeamMember : 'Unassigned',
    assigned_to: assigned ? supportTeamMember : null,
    assignedTo: assigned ? supportTeamMember : 'Unassigned',
  };
};

/** Decision ID format is NOT DEFINED — retain existing demo pattern. */
export const decisionIdFromCaseId = (caseId) => {
  const digits = String(caseId || '').replace(/\D/g, '');
  return `CMP-DEC-${(digits || '0000').slice(-4)}`;
};

export const display = (value) => dash(value);

export const isAssignedMember = (value) => {
  const name = formatAssignedDisplay(value);
  return Boolean(name && name !== 'Unassigned');
};

export const isDuringReview = (row) => {
  if (!row) return false;
  const status = normalizeQueueStatus(row.case_status || row.status || row.verification_status);
  if (status === 'Decided' || row.finalDecision?.outcome) return false;
  return (
    status === 'Under Review' &&
    isAssignedMember(row.support_team_member || row.supportTeamMember || row.assigned_to || row.assignedTo)
  );
};

const toQueueRow = (row) => {
  const coerced = coerceStatusAndAssignment(row);
  return {
    ...row,
    ...coerced,
    compliance_id: row.compliance_id || row.caseId,
    caseId: row.compliance_id || row.caseId,
    business_name: row.business_name || row.businessName,
    business_id: row.business_id || row.businessId,
    business_avatar: row.business_avatar || row.businessAvatar,
    compliance_type: row.compliance_type || row.complianceType || row.category,
    compliance_item: row.compliance_item || row.complianceItem || row.document_name,
    compliance_item_id: row.compliance_item_id || row.complianceItemId,
    trade_category: row.trade_category || row.tradeCategory,
    business_trade_category: row.business_trade_category || row.businessTradeCategory,
    case_type: row.case_type || row.caseType || 'New',
    submitted_at: row.submitted_at || row.submittedAt,
    last_updated: row.last_updated || row.lastUpdated,
    category: row.compliance_type || row.complianceType || row.category,
    document_name: row.compliance_item || row.complianceItem || row.document_name,
    handoffSucceeded: Boolean(row.handoffSucceeded),
    transferredOut: Boolean(row.transferredOut),
  };
};

/** Spec sample rows. */
export const QUEUE_SPEC_SAMPLES = [
  {
    compliance_id: 'CMP-004281',
    business_name: 'Apex Plumbing',
    business_id: 'BUS-001284',
    abn: '12 345 678 901',
    account_holder_legal_name: 'Mark Taylor',
    account_status: 'Active',
    compliance_type: 'Licence',
    compliance_item_id: 'LIC-0002',
    compliance_item: 'Plumbing Licence',
    trade_category: 'Plumbing',
    business_trade_category: 'Plumbing',
    case_type: 'Renewal',
    support_team_member: 'Jordan Lee',
    case_status: 'Under Review',
    submitted_at: parseSampleDate(3, 'Apr', 2026, 9, 0, false),
    last_updated: parseSampleDate(3, 'Apr', 2026, 10, 5, false),
  },
  {
    compliance_id: 'CMP-004282',
    business_name: 'Bright Spark Electrical',
    business_id: 'BUS-001285',
    account_status: 'Active',
    compliance_type: 'Ticket',
    compliance_item_id: 'TKT-0001',
    compliance_item: 'White Card',
    trade_category: 'All Trades',
    business_trade_category: 'Electrical',
    case_type: 'New',
    support_team_member: null,
    case_status: 'New',
    submitted_at: parseSampleDate(3, 'Apr', 2026, 9, 18, false),
    last_updated: parseSampleDate(3, 'Apr', 2026, 9, 18, false),
  },
  {
    compliance_id: 'CMP-004283',
    business_name: 'Urban Gas Works',
    business_id: 'BUS-001286',
    account_status: 'Active',
    compliance_type: 'Licence',
    compliance_item_id: 'LIC-0003',
    compliance_item: 'Gas Fitting Licence',
    trade_category: 'Plumbing',
    business_trade_category: 'Plumbing',
    case_type: 'New',
    support_team_member: null,
    case_status: 'New',
    submitted_at: parseSampleDate(2, 'Apr', 2026, 16, 10, true),
    last_updated: parseSampleDate(3, 'Apr', 2026, 8, 35, false),
  },
  {
    compliance_id: 'CMP-004284',
    business_name: 'Metro Building Group',
    business_id: 'BUS-001287',
    account_status: 'Active',
    compliance_type: 'Licence',
    compliance_item_id: 'LIC-0001',
    compliance_item: 'Electrical Licence',
    trade_category: 'Electrical',
    business_trade_category: 'Building',
    case_type: 'Replacement',
    support_team_member: 'Jordan Lee',
    case_status: 'Decided',
    submitted_at: parseSampleDate(2, 'Apr', 2026, 14, 35, true),
    last_updated: parseSampleDate(3, 'Apr', 2026, 11, 20, false),
    finalOutcome: 'Approved',
    decisionId: 'CMP-DEC-4284',
  },
];

export const findQueueSpecSample = (complianceId) =>
  QUEUE_SPEC_SAMPLES.find((row) => row.compliance_id === complianceId) || null;

const buildExtraQueueRows = () => {
  const businessNames = [
    'Northside Plumbing Co.',
    'Bolt Electrical Services',
    'Coastal Gas & Heating',
    'Modern Painters Collective',
    'Master Builders Guild',
    'Green Lawn Care Pty Ltd',
    'Smart Locksmith Co.',
    'Elite Flooring Professionals',
    'QuickFix Tiling & Reno',
    'Rapid Roofing Group',
    'Ace Carpentry Solutions',
    'Top Tilers & Bathrooms',
    'Clean Windows Pro',
  ];
  const extra = [];
  const now = new Date();
  for (let i = 0; i < 40; i++) {
    const n = 4286 + i;
    const complianceType = COMPLIANCE_TYPES[i % COMPLIANCE_TYPES.length];
    const statusCycle = COMPLIANCE_QUEUE_STATUSES[i % COMPLIANCE_QUEUE_STATUSES.length];
    const caseType = CASE_TYPES[i % CASE_TYPES.length];
    const items = COMPLIANCE_ITEMS_BY_TYPE[complianceType];
    const item = items[i % items.length];
    const businessTradeCategory = TRADE_CATEGORIES[i % TRADE_CATEGORIES.length];
    const assigned = statusCycle !== 'New';
    const support_team_member = assigned
      ? SUPPORT_TEAM_MEMBERS[i % SUPPORT_TEAM_MEMBERS.length]
      : null;

    const submittedDate = new Date(now);
    submittedDate.setDate(submittedDate.getDate() - (i % 90));
    submittedDate.setHours(9 + (i % 8), (i * 17) % 60, 0, 0);

    const lastUpdated = new Date(submittedDate);
    lastUpdated.setDate(lastUpdated.getDate() + (i % 10));
    lastUpdated.setHours(10 + (i % 7), (i * 13) % 60, 0, 0);

    extra.push({
      compliance_id: `CMP-${String(n).padStart(6, '0')}`,
      business_name: businessNames[i % businessNames.length],
      business_id: `BUS-${String(200 + i).padStart(6, '0')}`,
      business_avatar: `https://i.pravatar.cc/150?img=${(i % 70) + 1}`,
      compliance_type: complianceType,
      compliance_item_id: item.id,
      compliance_item: item.name,
      trade_category: item.tradeCategory || businessTradeCategory,
      business_trade_category: businessTradeCategory,
      case_type: caseType,
      support_team_member,
      case_status: statusCycle,
      submitted_at: submittedDate.toISOString(),
      last_updated: lastUpdated.toISOString(),
    });
  }
  return extra;
};

const cloneCase = (row) => JSON.parse(JSON.stringify(row));

let queueState = [
  ...QUEUE_SPEC_SAMPLES.map((row, i) => ({
    ...row,
    business_avatar: `https://i.pravatar.cc/150?img=${(i % 70) + 1}`,
  })),
  ...buildExtraQueueRows(),
].map((row) => toQueueRow(row));

export const collectQueueCases = () =>
  queueState
    .filter((row) => !row.handoffSucceeded && !row.transferredOut)
    .map((row) => cloneCase(toQueueRow(row)))
    .sort(
      (a, b) =>
        new Date(b.submitted_at || 0).getTime() - new Date(a.submitted_at || 0).getTime()
    );

export const findQueueCase = (caseId) => {
  if (!caseId) return null;
  const found = queueState.find(
    (row) =>
      String(row.compliance_id) === String(caseId) &&
      !row.handoffSucceeded &&
      !row.transferredOut
  );
  return found ? cloneCase(toQueueRow(found)) : null;
};

export const upsertQueueCase = (next) => {
  const id = next?.compliance_id || next?.caseId;
  if (!id) return null;
  const saved = toQueueRow({ ...next, compliance_id: id, caseId: id });
  const index = queueState.findIndex((row) => String(row.compliance_id) === String(id));
  if (index >= 0) queueState[index] = saved;
  else queueState.push(saved);
  return cloneCase(saved);
};

export const markQueueHandoffSuccess = (caseId) => {
  const index = queueState.findIndex((row) => String(row.compliance_id) === String(caseId));
  if (index < 0) return null;
  queueState[index] = { ...queueState[index], handoffSucceeded: true, case_status: 'Decided' };
  return cloneCase(queueState[index]);
};

export const markQueueTransferred = (caseId) => {
  const index = queueState.findIndex((row) => String(row.compliance_id) === String(caseId));
  if (index < 0) return null;
  queueState[index] = {
    ...queueState[index],
    transferredOut: true,
    handoffSucceeded: false,
  };
  return cloneCase(queueState[index]);
};

export const matchesQueueSearch = (row, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.compliance_id,
    row.business_name,
    row.business_id,
    row.compliance_item,
    row.compliance_item_id,
    row.compliance_type,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

export const matchesQueueFilters = (row, filters = {}) => {
  const status = normalizeQueueStatus(row.case_status || row.status);
  if (filters.status && filters.status !== 'All' && status !== filters.status) return false;
  if (filters.type && filters.type !== 'All' && row.compliance_type !== filters.type) return false;
  if (filters.supportTeam && filters.supportTeam !== 'All') {
    const member = normalizeSupportTeam(
      row.support_team_member || row.supportTeamMember || row.assigned_to
    );
    if (filters.supportTeam === 'Unassigned') {
      if (member !== 'Unassigned') return false;
    } else if (member !== filters.supportTeam) {
      return false;
    }
  }
  return true;
};

export const getCaseStatusFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...COMPLIANCE_QUEUE_STATUSES.map((value) => ({ value, label: value })),
];

export const getTypeFilterOptions = () => [
  { value: 'All', label: 'All' },
  ...COMPLIANCE_TYPES.map((value) => ({ value, label: value })),
];

export const getSupportTeamFilterOptions = (rows = []) => {
  const names = new Set(['Unassigned']);
  rows.forEach((row) => {
    const name = normalizeSupportTeam(
      row.support_team_member || row.supportTeamMember || row.assigned_to
    );
    if (name && name !== 'Unassigned') names.add(name);
  });
  SUPPORT_TEAM_MEMBERS.forEach((name) => names.add(name));
  return [
    { value: 'All', label: 'All' },
    ...[...names].sort().map((value) => ({ value, label: value })),
  ];
};

/** @deprecated Prefer collectQueueCases */
export const generateDummyDocuments = () => collectQueueCases();
